import {useEffect,useRef,useState,type FormEvent} from 'react';
import {SearchPersonRow,type SearchPersonDetails} from './SearchPersonRow';

export type StaffSearchCustomer={
  customerId:string;displayName:string;homeLocationCode:string|null;
  actorId?:string|null;actorKind?:'TEAM_MEMBER'|'CUSTOMER'|null;
  status?:'ACTIVE'|'DISABLED';
  email?:string|null;phone?:string|null;
};
export type StaffSearchTeamMember={
  staffId:string;displayName:string;locationCodes:string[];helmUserId?:string|null;
  email?:string|null;phone?:string|null;
};
type SearchResponse={ok:boolean;data?:{
  customers:StaffSearchCustomer[];hasMore:boolean;
}};
type Props={
  locationCode:string;
  onSelectCustomer:(customer:StaffSearchCustomer)=>void;
  onSelectTeamMember:(teamMember:StaffSearchTeamMember)=>void;
};
type RecentProfile={kind:'customer'|'team';id:string;displayName:string;status?:'ACTIVE'|'DISABLED';homeLocationCode?:string|null;locationCodes?:string[];helmUserId?:string|null};
// Contact previews stay only in ephemeral tab memory, never server history.
const transientRecentContacts=new Map<string,SearchPersonDetails>();
export function clearTransientRecentContacts(){transientRecentContacts.clear();}
const contactKey=(kind:'customer'|'team',id:string)=>kind+':'+id;
type RecentsResult={ok:boolean;data?:{recents:RecentProfile[]}};

const commandTypes:{[key:string]:string}={
  't':'Tasks','tg':'Task generators','gc':'Gift cards','cc':'Payment card lookup',
};
export function GlobalStaffSearch({locationCode,onSelectCustomer,onSelectTeamMember}:Props){
  const [term,setTerm]=useState('');
  const [focused,setFocused]=useState(false);
  const [recents,setRecents]=useState<RecentProfile[]>([]);
  const [matches,setMatches]=useState<StaffSearchCustomer[]>([]);
  const [teamMembers,setTeamMembers]=useState<StaffSearchTeamMember[]>([]);
  const [hasMore,setHasMore]=useState(false);
  const [status,setStatus]=useState<'idle'|'searching'|'ready'|'denied'|'error'>('idle');
  const anchor=useRef<HTMLDivElement|null>(null);
  const lastLocation=useRef(locationCode);
  const command=/^(tg|gc|cc|t)\s+/i.exec(term.trim());
  const special=command?.[1].toLowerCase()??null;
  const valid=term.trim().length>=2&&!special;

  useEffect(()=>{
    // Each location switch and focus reauthorizes the recent five against
    // the current staff session and today's target/location permissions.
    const controller=new AbortController();
    setRecents([]);
    void fetch('/api/v1/staff/search/recents?'+new URLSearchParams({
      location:locationCode,
    }),{signal:controller.signal,credentials:'same-origin',cache:'no-store'})
      .then(async response=>{
        if(!response.ok)throw Error('Recent profiles unavailable');
        const data=await response.json() as RecentsResult;
        if(!data.ok||!Array.isArray(data.data?.recents))
          throw Error('Invalid recent profiles');
        if(!controller.signal.aborted)setRecents(data.data.recents.slice(0,5));
      }).catch(()=>{if(!controller.signal.aborted)setRecents([]);});
    return ()=>controller.abort();
  },[locationCode,focused]);

  useEffect(()=>{
    // A search input can receive real user typing immediately after mounting.
    // Clearing it in the first passive effect discards that input (especially
    // under React Strict Mode), so only clear when location really changes.
    if(lastLocation.current===locationCode)return;
    lastLocation.current=locationCode;
    clearTransientRecentContacts();
    setRecents([]);
    setTerm('');setMatches([]);setTeamMembers([]);
    setStatus('idle');setFocused(false);
  },[locationCode]);
  useEffect(()=>{
    if(!focused||!valid){
      setMatches([]);setTeamMembers([]);setHasMore(false);setStatus('idle');return;
    }
    const controller=new AbortController();
    setStatus('searching');
    // The same authorized staff lookup used for creating bookings.
    // Browser caches and persistent storage are prohibited for contact history.
    const timeout=setTimeout(()=>{
      const params=new URLSearchParams({location:locationCode,q:term.trim()});
      // Both authorized datasets are searched. A Team Member is displayed
      // as a Team Member, never silently promoted to a customer record.
      void Promise.all([
        fetch('/api/v1/staff/customers?'+params,{
          signal:controller.signal,credentials:'same-origin',cache:'no-store',
        }),
        fetch('/api/v1/staff/team/search?'+params,{
          signal:controller.signal,credentials:'same-origin',cache:'no-store',
        }),
      ]).then(async([customerResponse,teamResponse])=>{
        if(controller.signal.aborted)return;
        if([customerResponse.status,teamResponse.status].some(x=>x===401||x===403)){
          setStatus('denied');setMatches([]);setTeamMembers([]);return;
        }
        if(!customerResponse.ok||!teamResponse.ok)throw Error('unavailable');
        const customers=await customerResponse.json() as SearchResponse;
        const team=await teamResponse.json() as {ok:boolean;data?:{
          teamMembers:StaffSearchTeamMember[];hasMore:boolean;
        }};
        if(!customers.ok||!customers.data||!team.ok||!team.data)
          throw Error('unavailable');
        if(controller.signal.aborted)return;
        // One person, one result. The employee's booking-ledger customer
        // record is NOT another user and must not produce a duplicate
        // entry with the same photo but missing contact information.
        const people=new Set(team.data.teamMembers.map(member=>member.staffId));
        setMatches(customers.data.customers.filter(customer=>
          (customer.status==='DISABLED'||customer.actorKind!=='TEAM_MEMBER')&&
          (!customer.actorId||!people.has(customer.actorId))));
        setTeamMembers(team.data.teamMembers);
        setHasMore(customers.data.hasMore||team.data.hasMore);
        setStatus('ready');
      }).catch(()=>{if(!controller.signal.aborted){
        setMatches([]);setTeamMembers([]);setStatus('error');
      }});
    },250);
    return ()=>{controller.abort();clearTimeout(timeout);};
  },[term,locationCode,focused,valid]);

  const remember=(item:RecentProfile)=>{
    // Save only verified opaque target IDs. No browser sessionStorage, no
    // cross-account name history, no success display before server accepts.
    void fetch('/api/v1/staff/search/recents',{
      method:'POST',credentials:'same-origin',cache:'no-store',
      headers:{'content-type':'application/json'},
      body:JSON.stringify({location:locationCode,kind:item.kind,id:item.id}),
    }).then(async response=>{
      if(!response.ok)throw Error('Recent person unavailable');
      const result=await response.json() as RecentsResult;
      if(!result.ok||!Array.isArray(result.data?.recents))
        throw Error('Recent person not stored');
      if(lastLocation.current===locationCode)
        setRecents(result.data.recents.slice(0,5));
    }).catch(()=>{/* Navigation still works when private recents cannot save. */});
  };
  const select=(customer:StaffSearchCustomer,details?:SearchPersonDetails)=>{
    if(details)transientRecentContacts.set(contactKey('customer',customer.customerId),details);
    remember({kind:'customer',id:customer.customerId,displayName:customer.displayName,
      homeLocationCode:customer.homeLocationCode,status:customer.status});
    setTerm('');setFocused(false);setMatches([]);setTeamMembers([]);setStatus('idle');
    onSelectCustomer(customer);
  };
  const selectTeam=(team:StaffSearchTeamMember,details?:SearchPersonDetails)=>{
    if(details)transientRecentContacts.set(contactKey('team',team.staffId),details);
    remember({kind:'team',id:team.staffId,displayName:team.displayName,
      locationCodes:team.locationCodes,helmUserId:team.helmUserId});
    setTerm('');setFocused(false);setMatches([]);setTeamMembers([]);
    setStatus('idle');
    onSelectTeamMember(team);
  };
  const submit=(event:FormEvent<HTMLFormElement>)=>{
    event.preventDefault();
    if(!valid)return;
    setFocused(true);
  };
  return <div className="ecfs-global-search-wrap" ref={anchor}
    onBlur={event=>{
      if(!event.currentTarget.contains(event.relatedTarget))setFocused(false);
    }}>
    <form className="ecfs-top-search" role="search" onSubmit={submit}>
      <svg className="ecfs-search-lens" aria-hidden="true"
        width="14" height="14" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <circle cx="10.8" cy="10.8" r="6.3"/>
        <path d="m15.5 15.5 5.1 5.1"/>
      </svg>
      <input type="search" aria-label="Global customer search" placeholder="Search"
        maxLength={80} value={term} onFocus={()=>setFocused(true)}
        onChange={event=>setTerm(event.target.value)}/>
    </form>
    {focused&&(valid||special||recents.length>0)&&<div className="ecfs-search-suggestions" role="region"
      aria-label="Global search suggestions">
      {special
        ?<p role="status">{commandTypes[special]} search is not connected in staging yet.
          No results can be shown for this prefix.</p>
        :valid
          ?<>
            {status==='searching'&&<p role="status">Searching…</p>}
            {status==='denied'&&<p role="status">Sign in with customer lookup access.</p>}
            {status==='error'&&<p role="status">Search temporarily unavailable.</p>}
            {status==='ready'&&matches.length===0&&teamMembers.length===0&&
              <p role="status">No matching results.</p>}
            {status==='ready'&&matches.map(customer=>
              <SearchPersonRow key={customer.customerId}
                person={{kind:'customer',id:customer.customerId,
                  displayName:customer.displayName,actorId:customer.actorId,
                  status:customer.status,email:customer.email,phone:customer.phone}}
                locationCode={locationCode}
                onSelect={details=>select(customer,details)}
                ariaLabel={'Open customer '+customer.displayName}/>)}
            {status==='ready'&&teamMembers.map(team=>
              <SearchPersonRow key={'team-'+team.staffId}
                person={{kind:'team',id:team.staffId,
                  displayName:team.displayName,email:team.email,phone:team.phone}}
                locationCode={locationCode}
                onSelect={details=>selectTeam(team,details)}
                ariaLabel={'Open Team Member '+team.displayName}/>)}
            {status==='ready'&&hasMore&&
              <p>More matches exist. Refine your search.</p>}
          </>
          :<>
            {recents.map(item=><SearchPersonRow
              key={item.kind+'-'+item.id}
              person={{kind:item.kind,id:item.id,displayName:item.displayName,
                status:item.status,...transientRecentContacts.get(contactKey(item.kind,item.id))}}
              recent locationCode={locationCode}
              onSelect={details=>item.kind==='team'?selectTeam({staffId:item.id,
                displayName:item.displayName,locationCodes:item.locationCodes??[],
                helmUserId:item.helmUserId??null},details):
                select({customerId:item.id,displayName:item.displayName,
                  homeLocationCode:item.homeLocationCode??null,status:item.status},details)}
              ariaLabel={'Recently opened '+item.displayName}/>)}
          </>}
    </div>}
  </div>;
}
